import { useEffect, useState } from "react";
import Button from "../../../components/Button";
import { useNavigate, useParams } from "react-router-dom";
import {
  apiOnlineMenuUrl,
  executeRequest,
  getApplicationToken,
} from "../../../functions/requests";
import IconLoadingWave from "../../../icons/IconLoadingWave";
import List from "../../../components/List";
import Spacer from "../../../components/Spacer";
import FormInput from "../../../components/FormInput";

export default function OnlineMenuOrders({
  showInProgress = false,
  showAccepted = false,
  showRejected = false,
}) {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const [menuSettings, setMenuSettings] = useState(null);
  const [menuSetting, setMenuSetting] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const getMenuSettings = async () => {
    await executeRequest(
      `${apiOnlineMenuUrl}menu-settings`,
      "GET",
      null,
      {
        Authorization: "Bearer " + getApplicationToken("online_menu"),
      },
      async (data) => {
        setMenuSettings(data);
        setLoading(false);
      },
      (error) => {
        console.log(error);
      }
    );
    setLoading(false);
  };

  const getMenuSetting = async () => {
    await executeRequest(
      `${apiOnlineMenuUrl}menu-settings/${uuid}`,
      "GET",
      null,
      {
        Authorization: "Bearer " + getApplicationToken("online_menu"),
      },
      async (data) => {
        setMenuSetting(data);
        setLoading(false);
      },
      (error) => {
        console.log(error);
      }
    );
    setLoading(false);
  };

  const getInProgressOrders = async () => {
    await executeRequest(
      `${apiOnlineMenuUrl}orders/${uuid}/pending`,
      "GET",
      null,
      {
        Authorization: "Bearer " + getApplicationToken("online_menu"),
      },
      async (data) => {
        console.log(data);
        setOrders(data.data);
      },
      (error) => {
        console.log(error);
      }
    );
    setLoading(false);
  };

  const getAcceptedOrders = async () => {
    setLoading(true);
    await executeRequest(
      `${apiOnlineMenuUrl}orders/${uuid}/accepted`,
      "GET",
      null,
      {
        Authorization: "Bearer " + getApplicationToken("online_menu"),
      },
      async (data) => {
        console.log(data);
        setOrders(data.data);
      },
      (error) => {
        console.log(error);
      }
    );
    setLoading(false);
  };

  const getRejectedOrders = async () => {
    setLoading(true);
    await executeRequest(
      `${apiOnlineMenuUrl}orders/${uuid}/rejected`,
      "GET",
      null,
      {
        Authorization: "Bearer " + getApplicationToken("online_menu"),
      },
      async (data) => {
        console.log(data);
        setOrders(data.data);
      },
      (error) => {
        console.log(error);
      }
    );
    setLoading(false);
  };

  const getOrdersConditionally = async () => {
    if (showInProgress) {
      getInProgressOrders();
    }
    if (showAccepted) {
      getAcceptedOrders();
    }
    if (showRejected) {
      getRejectedOrders();
    }
  };

  const acceptOrder = async (order) => {
    await executeRequest(
      `${apiOnlineMenuUrl}orders/${order.uuid}`,
      "POST",
      {
        accepted_at: Math.floor(Date.now() / 1000),
      },
      {
        Authorization: "Bearer " + getApplicationToken("online_menu"),
      },
      async (data) => {
        getOrdersConditionally();
      },
      (error) => {
        console.log(error);
      }
    );
  };

  const rejectOrder = async (order) => {
    await executeRequest(
      `${apiOnlineMenuUrl}orders/${order.uuid}`,
      "POST",
      {
        declined_at: Math.floor(Date.now() / 1000),
      },
      {
        Authorization: "Bearer " + getApplicationToken("online_menu"),
      },
      async () => {
        getOrdersConditionally();
      },
      (error) => {
        console.log(error);
      }
    );
  };

  const deleteOrder = async (order) => {
    await executeRequest(
      `${apiOnlineMenuUrl}orders/${order.uuid}`,
      "DELETE",
      null,
      {
        Authorization: "Bearer " + getApplicationToken("online_menu"),
      },
      async () => {
        getOrdersConditionally();
      },
      (error) => {
        console.log(error);
      }
    );
  };

  useEffect(() => {
    if (!uuid) {
      getMenuSettings();
    } else {
      getMenuSetting();

      let intervalId;

      // Initial load of tickets from loaded parameter
      if (showInProgress) {
        setLoading(true);
        getInProgressOrders();
        // Set up interval to fetch in-progress orders every 10 seconds
        intervalId = setInterval(() => {
          getInProgressOrders();
        }, 10000);
      } else if (showAccepted) {
        getAcceptedOrders();
      } else if (showRejected) {
        getRejectedOrders();
      }

      // Cleanup function to clear the interval
      return () => {
        if (intervalId) {
          clearInterval(intervalId);
        }
      };
    }
  }, [showInProgress, showAccepted, showRejected]);

  if (!uuid) {
    if (loading) {
      return (
        <>
          <IconLoadingWave />
        </>
      );
    }

    return (
      <>
        <h1 className="page-title">Menu Orders</h1>
        <Button
          type="underlined"
          text="< Back to homepage"
          onClicked={() => navigate("/projects/online-menu")}
        />
        <Spacer />
        <div className="instruction">
          Here you select the menu you want to view orders for. They will renew
          every 20 seconds, so you have live updates.
        </div>
        <Spacer />
        <div className="flex flex-direction-column flex-gap-m">
          <h2>Select menu to view orders</h2>
          <FormInput
            type="select"
            selectOptions={menuSettings}
            onChanged={(value) => {
              navigate("/projects/online-menu/orders/" + value);
            }}
          />
        </div>
      </>
    );
  }

  return (
    <>
      <h1 className="page-title">Menu Orders</h1>
      <Button
        type="underlined"
        text="< Back to menus"
        onClicked={() => navigate("/projects/online-menu/orders")}
      />
      <Spacer />
      <div className="instruction">
        New orders will appear here. Expect updates every 20 seconds (almost
        real-time).
      </div>
      <Spacer />
      {menuSetting !== null && (
        <div className="flex flex-gap-m">
          <h2>Orders for {menuSetting.title}</h2>
          <Button
            type="empty"
            text="Refresh"
            disabled={loading}
            onClicked={() => getOrdersConditionally()}
          />
        </div>
      )}
      <Spacer />
      <div className="flex flex-gap-s">
        <Button
          type="empty"
          active={showInProgress}
          text="Pending"
          disabled={showInProgress}
          onClicked={() => {
            setOrders([]);
            navigate("/projects/online-menu/orders/" + uuid);
          }}
        />
        <Button
          type="empty"
          active={showAccepted}
          text="Accepted"
          disabled={showAccepted}
          onClicked={() => {
            setOrders([]);
            navigate("/projects/online-menu/orders/" + uuid + "/accepted");
          }}
        />
        <Button
          type="empty"
          active={showRejected}
          text="Rejected"
          disabled={showRejected}
          onClicked={() => {
            setOrders([]);
            navigate("/projects/online-menu/orders/" + uuid + "/rejected");
          }}
        />
      </div>
      <Spacer />
      <List
        headers={["Table number", "Items", "Created at"]}
        itemType="menu-order"
        items={orders}
        isLoading={loading}
        onDelete={(item) => deleteOrder(item)}
        onAccept={
          !showAccepted
            ? (item) => {
                acceptOrder(item);
              }
            : null
        }
        onReject={
          !showRejected
            ? (item) => {
                rejectOrder(item);
              }
            : null
        }
      />
    </>
  );
}
